"use client";

import { useActionState, type ComponentProps, type ReactNode } from "react";
import { initialActionResult, type ActionResult } from "@/lib/action-result";

type ActionFormProps = Omit<ComponentProps<"form">, "action" | "children"> & {
  action: (formData: FormData) => Promise<ActionResult>;
  children: ReactNode;
  pendingMessage?: string;
};

/** Shared boundary for Server Action progress and expected domain errors. */
export function ActionForm({
  action,
  children,
  pendingMessage = "正在处理…",
  ...formProps
}: ActionFormProps) {
  const [result, formAction, pending] = useActionState(
    async (_previous: ActionResult, formData: FormData) => action(formData),
    initialActionResult,
  );

  return (
    <form {...formProps} action={formAction}>
      <fieldset disabled={pending} className="contents">
        {children}
      </fieldset>
      <div aria-live="polite" className="contents">
        {pending && <p className="text-xs text-[var(--color-muted)]">{pendingMessage}</p>}
        {!pending && !result.ok && (
          <p
            role="alert"
            className="rounded-lg border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 px-3 py-2 text-xs text-[var(--color-alert)]"
          >
            {result.message}
          </p>
        )}
        {!pending && result.ok && result.message && (
          <p role="status" className="text-xs text-[var(--color-accent)]">
            {result.message}
          </p>
        )}
      </div>
    </form>
  );
}
